import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { Platform } from 'react-native';
import type { GvSettings, Chapter, VerseItem, AvailableTranslation, Reciter } from '../types';

// On native, use AsyncStorage; on web, use localStorage (default)
let nativeStorage: StateStorage | undefined;
if (Platform.OS !== 'web') {
  const AsyncStorage = require('@react-native-async-storage/async-storage').default;
  nativeStorage = {
    getItem: (name: string) => AsyncStorage.getItem(name),
    setItem: (name: string, value: string) => AsyncStorage.setItem(name, value),
    removeItem: (name: string) => AsyncStorage.removeItem(name),
  };
}

export const ARABIC_FONTS = [
  { id: 0, name: 'KFGQ', value: 'KfgqpcHafsUthmanicScript' },
  { id: 1, name: 'Me Quran', value: 'me_quran' },
  { id: 2, name: 'Al Qalam', value: 'AlQalamQuranMajeed2' },
  { id: 3, name: 'Scheherazade', value: 'Scheherazade' },
] as const;

export const THEMES = [
  { id: 0, name: 'Light', value: 'light' },
  { id: 1, name: 'Dark', value: 'dark' },
  { id: 2, name: 'System', value: 'system' },
] as const;

export const VIEW_MODES = [
  { id: 0, name: 'Regular', value: 'regular' },
  { id: 1, name: 'Single Card', value: 'single-card' },
  { id: 2, name: 'Mushaf', value: 'mushaf' },
] as const;

export const RECITERS = [
  { id: 0,  name: 'Abdul Basit Mujawwad',      key: 'Abdul_Basit_Mujawwad_128kbps' },
  { id: 1,  name: 'Abdurrahmaan As-Sudais',    key: 'Abdurrahmaan_As-Sudais_64kbps' },
  { id: 2,  name: 'Abu Bakr Ash-Shaatree',     key: 'Abu_Bakr_Ash-Shaatree_64kbps' },
  { id: 3,  name: 'Al Husary',                 key: 'Husary_64kbps' },
  { id: 4,  name: 'Al Menshawi',               key: 'Menshawi_32kbps' },
  { id: 5,  name: 'Ayman Sowaid',              key: 'Ayman_Sowaid_64kbps' },
  { id: 6,  name: 'Hudhaify',                  key: 'Hudhaify_64kbps' },
  { id: 7,  name: 'Ibrahim Akhdar',            key: 'Ibrahim_Akhdar_64kbps' },
  { id: 8,  name: 'Khalefa Al Tunaiji',        key: 'khalefa_al_tunaiji_64kbps' },
  { id: 9,  name: 'Maher AlMuaiqly',           key: 'Maher_AlMuaiqly_64kbps' },
  { id: 10, name: 'Mishary Rashid Alafasy',    key: 'Alafasy_64kbps' },
  { id: 11, name: 'Saad Al Ghamdi',            key: 'Ghamadi_40kbps' },
  { id: 12, name: 'Yasser Ad-Dussary',         key: 'Yasser_Ad-Dussary_128kbps' },
] as const;

export const DEFAULT_SETTINGS: GvSettings = {
  arabic_font_size: '24',
  trans_font_size: '14',
  show_arabic: true,
  arabic_font: { ...ARABIC_FONTS[0] },
  current_script: 'ar-uth',
  lang_array: ['en-int', 'bn-byf'],
  theme: { ...THEMES[0] },
  auto_switch_theme: false,
  sort_chapters_reverse: false,
  current_ch: '',
  last_ch: '',
  last_vs: '',
  scroll_position: 0,
  current_start_vs: '',
  current_end_vs: '',
  view_mode: { ...VIEW_MODES[0] },
  remember_last_state: false,
  kahf_reminder: true,
  keep_screen_on: true,
  current_reciter: { ...RECITERS[3] },
};

interface AppState {
  settings: GvSettings;
  chapters: Chapter[];
  verses: Record<string, VerseItem>;
  availableTranslations: Record<string, AvailableTranslation>;
  availableScripts: Array<{ code: string; title: string }>;
  reciters: Reciter[];
  appTitle: string;
  online: boolean;
  isMobileAgent: boolean;
  cardInputType: string;
  cardInputValue: string;
  showNote: boolean;
  showNotifyModal: boolean;
  verseNoteTitle: string;
  verseFootnotes: string[];
  dontNotify: number;
  friday: number | string;
  leftDrawerOpen: boolean;
  rightDrawerOpen: boolean;
  onVersePage: boolean;

  setSettings: (s: Partial<GvSettings>) => void;
  setChapters: (c: Chapter[]) => void;
  setVerses: (v: Record<string, VerseItem>) => void;
  mergeVerses: (v: Record<string, VerseItem>) => void;
  setAvailableTranslations: (t: Record<string, AvailableTranslation>) => void;
  setAvailableScripts: (s: Array<{ code: string; title: string }>) => void;
  setReciters: (r: Reciter[]) => void;
  setAppTitle: (t: string) => void;
  setOnline: (o: boolean) => void;
  setCardInput: (type: string, value: string) => void;
  setShowNote: (s: boolean) => void;
  setShowNotifyModal: (s: boolean) => void;
  setVerseNoteTitle: (t: string) => void;
  setVerseFootnotes: (f: string[]) => void;
  setDontNotify: (v: number) => void;
  setOnVersePage: (v: boolean) => void;
  toggleLeftDrawer: () => void;
  toggleRightDrawer: () => void;
  closeDrawers: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      settings: { ...DEFAULT_SETTINGS },
      chapters: [],
      verses: {},
      availableTranslations: {},
      availableScripts: [],
      reciters: [],
      appTitle: '',
      online: Platform.OS === 'web' && typeof navigator !== 'undefined' ? navigator.onLine : true,
      isMobileAgent: Platform.OS !== 'web' ? true : (
        typeof navigator !== 'undefined'
          ? /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
          : false
      ),
      cardInputType: '',
      cardInputValue: '',
      showNote: false,
      showNotifyModal: false,
      verseNoteTitle: '',
      verseFootnotes: [],
      dontNotify: 0,
      friday: 0,
      leftDrawerOpen: false,
      rightDrawerOpen: false,
      onVersePage: false,

      setSettings: (s) =>
        set((state) => ({ settings: { ...state.settings, ...s } })),

      setChapters: (chapters) => set({ chapters }),

      setVerses: (verses) => set({ verses }),

      mergeVerses: (v) =>
        set((state) => ({ verses: { ...state.verses, ...v } })),

      setAvailableTranslations: (availableTranslations) =>
        set({ availableTranslations }),

      setAvailableScripts: (availableScripts) => set({ availableScripts }),

      setReciters: (reciters) => set({ reciters }),

      setAppTitle: (appTitle) => set({ appTitle }),

      setOnline: (online) => set({ online }),

      setCardInput: (cardInputType, cardInputValue) =>
        set({ cardInputType, cardInputValue }),

      setShowNote: (showNote) => set({ showNote }),

      setShowNotifyModal: (showNotifyModal) => set({ showNotifyModal }),

      setVerseNoteTitle: (verseNoteTitle) => set({ verseNoteTitle }),

      setVerseFootnotes: (verseFootnotes) => set({ verseFootnotes }),

      setDontNotify: (dontNotify) => set({ dontNotify }),

      setOnVersePage: (onVersePage) => set({ onVersePage }),

      toggleLeftDrawer: () =>
        set((state) => ({
          leftDrawerOpen: !state.leftDrawerOpen,
          rightDrawerOpen: false,
        })),

      toggleRightDrawer: () =>
        set((state) => ({
          rightDrawerOpen: !state.rightDrawerOpen,
          leftDrawerOpen: false,
        })),

      closeDrawers: () =>
        set({ leftDrawerOpen: false, rightDrawerOpen: false }),
    }),
    {
      name: 'gv-store',
      ...(nativeStorage ? { storage: createJSONStorage(() => nativeStorage!) } : {}),
      partialize: (state) => ({
        settings: state.settings,
        chapters: state.chapters,
        dontNotify: state.dontNotify,
        friday: state.friday,
        availableTranslations: state.availableTranslations,
        availableScripts: state.availableScripts,
        reciters: state.reciters,
      }),
    },
  ),
);
